import { useEffect, useState } from "react";
import { api, MONTHS_IT } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Thermometer, Sun, Cloud, Snowflake, Leaf, Sparkles, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const seasonIcon = {
  Estate: Sun, Primavera: Leaf, Autunno: Cloud, Inverno: Snowflake,
};

const REVEAL = { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function WeatherCorredino() {
  const { profile } = useProfile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!profile) return;
    let active = true;
    setLoading(true);
    setError(null);
    api
      .get("/ai/corredino", {
        params: { city: profile.city, month: profile.due_date.month, sex: profile.sex, profile_id: profile.id },
      })
      .then(({ data }) => active && setData(data))
      .catch(() => active && setError("Non siamo riusciti a recuperare il meteo per questa città."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [profile]);

  if (!profile) return null;
  const dd = profile.due_date;
  const born = new Date(dd.year, dd.month - 1, dd.day) <= new Date();
  const w = data?.weather;
  const Icon = w ? seasonIcon[w.season] || Sun : Sun;

  return (
    <section id="corredino" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12 lg:py-14" data-testid="weather-section">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8C7F78]">Consiglio AI</span>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825] mt-2">La taglia e il corredino giusti per il clima</h2>
        <p className="text-[#5C534E] mt-2 max-w-xl">
          In base alla temperatura media di <strong>{profile.city}</strong> {born ? "in questo mese" : "nel mese di nascita"}.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#8C7F78]" data-testid="weather-loading">
          <Loader2 className="animate-spin" size={18} /> Stiamo consultando il meteo e la nostra consulente...
        </div>
      )}
      {error && <div className="text-[#B85750]" data-testid="weather-error">{error}</div>}

      {w && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="weather-card">
          <motion.div
            {...REVEAL}
            className="lg:col-span-5 rounded-[1.75rem] p-7 text-white relative overflow-hidden"
            style={{ background: "linear-gradient(145deg,#9DC0DB,#4A7496)" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90 capitalize">{w.month_name} · {w.city}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Icon size={22} />
                  <span className="text-lg">{w.season}</span>
                </div>
              </div>
              <Thermometer size={28} className="opacity-90" />
            </div>
            <div className="mt-8 font-serif text-6xl">{w.avg}°</div>
            <div className="text-sm opacity-90">Temperatura media</div>
            {data.size && (
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-sm" data-testid="weather-size">
                {data.born ? "Taglia di adesso" : "Taglia per la nascita"}: <strong>{data.size}</strong>
              </div>
            )}
            <div className="mt-6 flex gap-6 text-sm">
              <div><div className="opacity-80">Minima</div><div className="text-xl">{w.min}°</div></div>
              <div><div className="opacity-80">Massima</div><div className="text-xl">{w.max}°</div></div>
            </div>
          </motion.div>

          <motion.div
            {...REVEAL}
            className="lg:col-span-7 rounded-[1.75rem] bg-white border border-[#EAE3DC] p-7"
          >
            <div className="flex items-center gap-2 text-[#C87D75] mb-3">
              <Sparkles size={18} />
              <span className="font-display font-medium">Il consiglio della nostra consulente</span>
            </div>
            <p className="text-[#5C534E] leading-relaxed" data-testid="ai-advice">{data.advice}</p>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[#8C7F78] mb-3">Capi consigliati</div>
              <div className="flex flex-wrap gap-2" data-testid="clothing-suggestions">
                {w.clothing.map((c) => (
                  <span key={c} className="rounded-full bg-[#F9ECE9] text-[#C87D75] text-sm px-4 py-1.5">{c}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
